import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { formatNumber, isoDate } from './shared/chartFormat';

const LEVELS = 5;
// Rows are Sunday-first like the reference GitHub graph; label a sparse set.
const LABELLED_ROWS = [0, 3, 5];

// PE-438: GitHub-style year heatmap from /calendar_chart (current year,
// non-zero days only). Clicking a day opens it in the calendar view.
export default class AppointmentYearHeatmap extends React.Component {
  constructor(props) {
    super(props);
    this.state = { counts: {}, max: 0, loading: true, error: false };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) this.fetchData();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  fetchData() {
    const requestId = (this.requestId = (this.requestId || 0) + 1);
    this.setState({ loading: true, error: false });

    fetch('/api/appointments/calendar_chart' + (this.props.query || ''))
      .then(res => (res.ok ? res.json() : Promise.reject(res)))
      .then(data => {
        if (this.unmounted || requestId !== this.requestId) return;
        const counts = {};
        let max = 0;
        (Array.isArray(data) ? data : []).forEach(entry => {
          counts[entry.day] = entry.value;
          max = Math.max(max, entry.value);
        });
        this.setState({ counts, max, loading: false });
      })
      .catch(() => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ counts: {}, max: 0, loading: false, error: true });
      });
  }

  level(value) {
    const { max } = this.state;
    if (!value || max === 0) return 0;
    return Math.max(1, Math.ceil((value / max) * LEVELS));
  }

  openDay(day) {
    const url = `/appointments?view=calendar&date=${day}`;
    if (window.Turbolinks) {
      window.Turbolinks.visit(url);
    } else {
      window.location.assign(url);
    }
  }

  yearDays() {
    const year = new Date().getFullYear();
    const days = [];
    for (let date = new Date(year, 0, 1); date.getFullYear() === year; date.setDate(date.getDate() + 1)) {
      days.push({ key: isoDate(date), dow: date.getDay(), month: date.getMonth() });
    }
    return days;
  }

  renderScale() {
    const { labels } = this.props;

    return (
      <div className="appt-stats__scale">
        <span>{labels.year_scale_low}</span>
        {Array.from({ length: LEVELS }, (unused, index) => (
          <i className={`appt-stats__scale-swatch appt-stats__hm-cell--l${index + 1}`} key={index} />
        ))}
        <span>{labels.year_scale_high}</span>
      </div>
    );
  }

  renderGrid() {
    const { labels, locale } = this.props;
    const { counts } = this.state;
    const weekdaysShort = labels.weekdays_short.split(',');
    const months = labels.months_short.split(',');

    const days = this.yearDays();
    const startPad = days[0].dow;
    const totalWeeks = Math.ceil((days.length + startPad) / 7);
    const monthWeeks = months.map((name, month) => {
      const index = days.findIndex(day => day.month === month);
      return { name, week: Math.floor((index + startPad) / 7) };
    });

    return (
      <div className="appt-stats__heatmap-scroll">
        <div className="appt-stats__heatmap">
          <div className="appt-stats__hm-months">
            {monthWeeks.map(month => (
              <span key={month.name} style={{ left: `${(month.week / totalWeeks) * 100}%` }}>{month.name}</span>
            ))}
          </div>
          <div className="appt-stats__hm-body">
            <div className="appt-stats__hm-days">
              {weekdaysShort.map((name, row) => (
                <span key={name}>{LABELLED_ROWS.indexOf(row) !== -1 ? name : ''}</span>
              ))}
            </div>
            <div className="appt-stats__hm-grid">
              {Array.from({ length: startPad }, (unused, index) => (
                <span className="appt-stats__hm-cell appt-stats__hm-cell--blank" key={`pad-${index}`} />
              ))}
              {days.map(day => {
                const value = counts[day.key] || 0;
                return (
                  <button
                    type="button"
                    className={`appt-stats__hm-cell appt-stats__hm-cell--l${this.level(value)}`}
                    key={day.key}
                    title={`${day.key} · ${formatNumber(value, locale)}`}
                    aria-label={`${day.key} · ${formatNumber(value, locale)}`}
                    onClick={() => this.openDay(day.key)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { labels } = this.props;
    const { loading, error, max } = this.state;

    return (
      <div>
        <div className="appt-stats__card-head">
          <div>
            <h4 className="card-title mb-1">{labels.year_title}</h4>
            <p className="appt-stats__card-subtitle">{labels.year_subtitle}</p>
          </div>
          {!loading && !error && this.renderScale()}
        </div>

        {loading ? (
          <div style={{ padding: '12px 0' }}>
            <Skeleton height={150} borderRadius={14} />
          </div>
        ) : error ? (
          <div className="text-center text-muted py-5">{labels.load_error}</div>
        ) : max === 0 ? (
          <div className="text-center text-muted py-5">{labels.no_data}</div>
        ) : (
          this.renderGrid()
        )}
      </div>
    );
  }
}
